/**
 * Reset Password page — /reset-password?token=<signed-jwt>
 *
 * Reads the token from the URL query, asks the user for a new password (twice),
 * and POSTs to /api/auth/reset-password. On success, redirects to /login with
 * a success toast so the user re-authenticates with their new password.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api/client';

const strongEnough = (pw) =>
  typeof pw === 'string' && pw.length >= 8 && /[A-Za-z]/.test(pw) && /\d/.test(pw);

const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) setError('Reset link is missing its token — please request a new one.');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) { toast.error('Passwords do not match.'); return; }
    if (!strongEnough(pw)) {
      toast.error('Password must be at least 8 characters and contain a letter and a digit.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      toast.success('Password updated. Please sign in with your new password.');
      navigate('/login', { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? (detail[0]?.msg || 'Invalid input')
          : (err.message || 'Could not reset password.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <Link to="/login" data-testid="reset-back-link" className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-[#F46A25] font-semibold mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to sign in
      </Link>

      <div className="bg-white border border-neutral-200 rounded-sm p-6 md:p-8">
        <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center mb-4">
          <ShieldCheck className="w-6 h-6 text-[#F46A25]" />
        </div>
        <h1 className="text-2xl font-black text-[#12263A] mb-2">Choose a new password</h1>
        <p className="text-sm text-neutral-600 mb-4">
          Enter a password at least 8 characters long with a letter and a digit.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-xs text-red-800 mb-4 flex items-start gap-2" data-testid="reset-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#12263A] mb-1">New password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                value={pw}
                onChange={e => setPw(e.target.value)}
                type="password"
                required
                minLength={8}
                autoFocus
                data-testid="reset-password-input"
                className="w-full border border-neutral-300 rounded-sm py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:border-[#F46A25]"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-[#12263A] mb-1">Confirm new password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <input
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                type="password"
                required
                minLength={8}
                data-testid="reset-password-confirm"
                className="w-full border border-neutral-300 rounded-sm py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:border-[#F46A25]"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || !token}
            data-testid="reset-submit-btn"
            className="w-full bg-[#F46A25] hover:bg-[#e05a15] text-white font-bold py-3 rounded-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</> : 'Update password'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
